/**
 * RepoDetailsPage
 * Detailed view of a connected repository showing generated agents, skills,
 * and the ability to edit, delete, and push changes to the repo.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Github,
  Bot,
  BookOpen,
  RefreshCw,
  GitPullRequest,
  Edit3,
  Trash2,
  ChevronDown,
  ChevronUp,
  FileCode,
  Layers,
  Database,
  Clock,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import { AppLayout } from '../../components/layout';
import { Button, Badge, Modal, Input, Textarea } from '../../components/ui';
import { EditAgentModal } from '../../components/developer-agents/EditAgentModal';
import { EditSkillModal } from '../../components/developer-agents/EditSkillModal';
import { PushToRepoModal } from '../../components/developer-agents/PushToRepoModal';
import { developerAgentApi } from '../../services/api';
import type { ConnectedRepo, GeneratedAgent, GeneratedSkill, CodebaseAnalysis } from '../../types';
import styles from './RepoDetailsPage.module.css';

export const RepoDetailsPage: React.FC = () => {
  const { repoId } = useParams<{ repoId: string }>();
  const navigate = useNavigate();

  // Data state
  const [repo, setRepo] = useState<ConnectedRepo | null>(null);
  const [agents, setAgents] = useState<GeneratedAgent[]>([]);
  const [skills, setSkills] = useState<GeneratedSkill[]>([]);
  const [analysis, setAnalysis] = useState<CodebaseAnalysis | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'agents' | 'skills' | 'overview'>('agents');
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);

  // Modal state
  const [editingAgent, setEditingAgent] = useState<GeneratedAgent | null>(null);
  const [editingSkill, setEditingSkill] = useState<GeneratedSkill | null>(null);
  const [showPushModal, setShowPushModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'agent' | 'skill'; id: string; name: string } | null>(null);

  // Operation state
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  /** Load all page data */
  const loadData = useCallback(async () => {
    if (!repoId) return;
    const decodedRepoId = decodeURIComponent(repoId);
    setIsLoading(true);
    setError(null);

    try {
      // Load connected repos to find this one
      const reposResponse = await developerAgentApi.getConnectedRepos();
      if (reposResponse.success && reposResponse.data) {
        const foundRepo = reposResponse.data.find((r) => r.id === decodedRepoId);
        if (foundRepo) {
          setRepo(foundRepo);

          // Load agents and skills using the repo's fullName (owner/repo format)
          // NOTE: Do NOT encodeURIComponent here — the backend route is /repos/:owner/:repo/agents
          // so the slash in fullName must remain as a real path separator.
          const [agentsRes, skillsRes] = await Promise.all([
            developerAgentApi.getRepoAgents(foundRepo.fullName),
            developerAgentApi.getRepoSkills(foundRepo.fullName),
          ]);

          if (agentsRes.success && agentsRes.data) {
            setAgents(agentsRes.data);
          }
          if (skillsRes.success && skillsRes.data) {
            setSkills(skillsRes.data);
          }

          // Load analysis result if available
          if (foundRepo.analysisId) {
            try {
              const analysisRes = await developerAgentApi.getAnalysisResult(foundRepo.analysisId);
              if (analysisRes.success && analysisRes.data) {
                setAnalysis(analysisRes.data);
              }
            } catch {
              // Analysis might not be available — non-critical
            }
          }
        } else {
          setError('Repository not found');
        }
      }
    } catch (err) {
      console.error('Failed to load repo details:', err);
      setError('Failed to load repository details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [repoId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /** Show temporary success toast */
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  /** Handle re-sync / re-analyze */
  const handleSync = async () => {
    if (!repo) return;
    setIsSyncing(true);
    try {
      await developerAgentApi.syncRepo(repo.fullName);
      showSuccess('Re-analysis started. This may take a moment...');
      setTimeout(() => loadData(), 5000);
    } catch (err) {
      console.error('Failed to sync repository:', err);
      setError('Failed to start re-analysis');
    } finally {
      setIsSyncing(false);
    }
  };

  /** Handle agent save */
  const handleAgentSave = async (updatedAgent: GeneratedAgent) => {
    try {
      const response = await developerAgentApi.updateAgent(updatedAgent.id, updatedAgent);
      if (response.success && response.data) {
        setAgents((prev) => prev.map((a) => (a.id === updatedAgent.id ? response.data! : a)));
        setEditingAgent(null);
        showSuccess(`Agent "${updatedAgent.name}" updated successfully`);
      }
    } catch (err) {
      console.error('Failed to update agent:', err);
      setError('Failed to update agent');
    }
  };

  /** Handle skill save */
  const handleSkillSave = async (updatedSkill: GeneratedSkill) => {
    try {
      const response = await developerAgentApi.updateSkill(updatedSkill.id, updatedSkill);
      if (response.success && response.data) {
        setSkills((prev) => prev.map((s) => (s.id === updatedSkill.id ? response.data! : s)));
        setEditingSkill(null);
        showSuccess(`Skill "${updatedSkill.name}" updated successfully`);
      }
    } catch (err) {
      console.error('Failed to update skill:', err);
      setError('Failed to update skill');
    }
  };

  /** Handle delete */
  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      if (deleteConfirm.type === 'agent') {
        const response = await developerAgentApi.deleteAgent(deleteConfirm.id);
        if (response.success) {
          setAgents((prev) => prev.filter((a) => a.id !== deleteConfirm.id));
          showSuccess(`Agent "${deleteConfirm.name}" deleted`);
        }
      } else {
        const response = await developerAgentApi.deleteSkill(deleteConfirm.id);
        if (response.success) {
          setSkills((prev) => prev.filter((s) => s.id !== deleteConfirm.id));
          showSuccess(`Skill "${deleteConfirm.name}" deleted`);
        }
      }
    } catch (err) {
      console.error('Failed to delete:', err);
      setError(`Failed to delete ${deleteConfirm.type}`);
    } finally {
      setIsDeleting(false);
      setDeleteConfirm(null);
    }
  };

  /** Handle push complete */
  const handlePushComplete = (prUrl?: string) => {
    setShowPushModal(false);
    if (prUrl) {
      showSuccess('Pull request created successfully!');
    }
  };

  /** Format relative time */
  const formatRelativeTime = (dateString?: string): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Loading state
  if (isLoading) {
    return (
      <AppLayout>
        <div className={styles.page}>
          <div className={styles.loadingState}>
            <RefreshCw size={24} className={styles.spinning} />
            <p>Loading repository details...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Error / not found state
  if (error && !repo) {
    return (
      <AppLayout>
        <div className={styles.page}>
          <div className={styles.errorState}>
            <AlertCircle size={32} />
            <p>{error}</p>
            <Button variant="secondary" onClick={() => navigate('/developer-agents')}>
              Back to Developer Agents
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!repo) return null;

  return (
    <AppLayout>
      <div className={styles.page}>
        {/* Success Toast */}
        {successMessage && (
          <div className={styles.successToast}>
            <CheckCircle size={16} />
            {successMessage}
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className={styles.errorBanner}>
            <AlertCircle size={16} />
            {error}
            <button className={styles.dismissBtn} onClick={() => setError(null)}>×</button>
          </div>
        )}

        {/* Header with back navigation */}
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate('/developer-agents')}>
            <ArrowLeft size={18} />
            Back to Developer Agents
          </button>

          <div className={styles.headerMain}>
            <div className={styles.repoInfo}>
              <div className={styles.repoTitle}>
                <Github size={24} />
                <h1>{repo.fullName}</h1>
                <Badge
                  variant={
                    repo.analysisStatus === 'complete' ? 'success' :
                    repo.analysisStatus === 'failed' ? 'error' : 'warning'
                  }
                >
                  {(repo.analysisStatus || 'pending').replace('-', ' ')}
                </Badge>
              </div>

              {/* Tech Stack Badges */}
              {repo.techStack && (
                <div className={styles.techStackRow}>
                  {repo.techStack.framework && (
                    <span className={styles.techBadge}>
                      <Layers size={12} />
                      {repo.techStack.framework}
                    </span>
                  )}
                  {(repo.techStack.languages || []).map((lang) => (
                    <span key={lang.name} className={styles.techBadge}>
                      <FileCode size={12} />
                      {lang.name} ({lang.percentage}%)
                    </span>
                  ))}
                  {repo.techStack.database && (
                    <span className={styles.techBadge}>
                      <Database size={12} />
                      {repo.techStack.database}
                    </span>
                  )}
                  {repo.techStack.runtime && (
                    <span className={styles.techBadge}>{repo.techStack.runtime}</span>
                  )}
                </div>
              )}

              <div className={styles.metaRow}>
                <span className={styles.metaItem}>
                  <Clock size={14} />
                  Last synced: {formatRelativeTime(repo.lastSyncedAt)}
                </span>
                <span className={styles.metaItem}>
                  <Bot size={14} />
                  {agents.length} Agents
                </span>
                <span className={styles.metaItem}>
                  <BookOpen size={14} />
                  {skills.length} Skills
                </span>
              </div>
            </div>

            <div className={styles.headerActions}>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<RefreshCw size={14} />}
                onClick={handleSync}
                isLoading={isSyncing}
              >
                Re-analyze
              </Button>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<GitPullRequest size={14} />}
                onClick={() => setShowPushModal(true)}
                disabled={repo.analysisStatus !== 'complete'}
              >
                Push to Repo
              </Button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'agents' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('agents')}
          >
            <Bot size={16} />
            Agents ({agents.length})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'skills' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('skills')}
          >
            <BookOpen size={16} />
            Skills ({skills.length})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'overview' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <Layers size={16} />
            Overview
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContent}>
          {/* ========= AGENTS TAB ========= */}
          {activeTab === 'agents' && (
            <div className={styles.agentsList}>
              {agents.length === 0 ? (
                <div className={styles.emptyTab}>
                  <Bot size={40} />
                  <h3>No agents generated yet</h3>
                  <p>Re-analyze the repository to generate agents</p>
                </div>
              ) : (
                agents.map((agent) => (
                  <div key={agent.id} className={styles.agentCard}>
                    <div className={styles.agentHeader}>
                      <div className={styles.agentInfo}>
                        <span className={styles.agentIcon}>{agent.icon || '🤖'}</span>
                        <div>
                          <h3 className={styles.agentName}>{agent.name}</h3>
                          <p className={styles.agentDesc}>{agent.description}</p>
                        </div>
                      </div>
                      <div className={styles.agentActions}>
                        <button
                          className={styles.iconBtn}
                          onClick={() => setEditingAgent(agent)}
                          title="Edit agent"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className={`${styles.iconBtn} ${styles.dangerBtn}`}
                          onClick={() => setDeleteConfirm({ type: 'agent', id: agent.id, name: agent.name })}
                          title="Delete agent"
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
                          className={styles.iconBtn}
                          onClick={() => setExpandedAgent(expandedAgent === agent.id ? null : agent.id)}
                          title="Toggle details"
                        >
                          {expandedAgent === agent.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className={styles.tagRow}>
                      {(agent.triggerKeywords || []).slice(0, 5).map((kw) => (
                        <span key={kw} className={styles.keyword}>{kw}</span>
                      ))}
                      {agent.attachedSkills?.length > 0 && (
                        <span className={styles.skillCount}>
                          {agent.attachedSkills.length} skill{agent.attachedSkills.length !== 1 ? 's' : ''} attached
                        </span>
                      )}
                    </div>

                    {/* Expanded Details */}
                    {expandedAgent === agent.id && (
                      <div className={styles.expandedDetails}>
                        <div className={styles.detailSection}>
                          <h4>System Instructions</h4>
                          <pre className={styles.codeBlock}>{agent.systemInstructions}</pre>
                        </div>

                        {agent.referenceFiles?.length > 0 && (
                          <div className={styles.detailSection}>
                            <h4>Reference Files</h4>
                            <div className={styles.fileList}>
                              {agent.referenceFiles.map((file) => (
                                <span key={file} className={styles.fileTag}>
                                  <FileCode size={12} />
                                  {file}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {agent.tools?.length > 0 && (
                          <div className={styles.detailSection}>
                            <h4>Tools</h4>
                            <div className={styles.tagRow}>
                              {agent.tools.map((tool) => (
                                <span key={tool} className={styles.toolTag}>{tool}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {agent.context && (
                          <div className={styles.detailSection}>
                            <h4>Context</h4>
                            <div className={styles.contextGrid}>
                              <div><strong>Framework:</strong> {agent.context.framework}</div>
                              <div><strong>Patterns:</strong> {(agent.context.patterns || []).join(', ')}</div>
                              <div><strong>Conventions:</strong> {(agent.context.conventions || []).join(', ')}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ========= SKILLS TAB ========= */}
          {activeTab === 'skills' && (
            <div className={styles.skillsList}>
              {skills.length === 0 ? (
                <div className={styles.emptyTab}>
                  <BookOpen size={40} />
                  <h3>No skills generated yet</h3>
                  <p>Re-analyze the repository to generate skills</p>
                </div>
              ) : (
                skills.map((skill) => (
                  <div key={skill.id} className={styles.skillCard}>
                    <div className={styles.skillHeader}>
                      <div className={styles.skillInfo}>
                        <h3 className={styles.skillName}>{skill.name}</h3>
                        <p className={styles.skillDesc}>{skill.description}</p>
                      </div>
                      <div className={styles.skillActions}>
                        <button
                          className={styles.iconBtn}
                          onClick={() => setEditingSkill(skill)}
                          title="Edit skill"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className={`${styles.iconBtn} ${styles.dangerBtn}`}
                          onClick={() => setDeleteConfirm({ type: 'skill', id: skill.id, name: skill.name })}
                          title="Delete skill"
                        >
                          <Trash2 size={16} />
                        </button>
                        <button
                          className={styles.iconBtn}
                          onClick={() => setExpandedSkill(expandedSkill === skill.id ? null : skill.id)}
                          title="Toggle details"
                        >
                          {expandedSkill === skill.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Categories and Meta */}
                    <div className={styles.tagRow}>
                      {(skill.categories || []).map((cat) => (
                        <span key={cat} className={styles.categoryTag}>{cat}</span>
                      ))}
                      <span className={styles.charCount}>{skill.charCount?.toLocaleString() || 0} chars</span>
                      {skill.usedByAgents?.length > 0 && (
                        <span className={styles.usageInfo}>
                          Used by {skill.usedByAgents.length} agent{skill.usedByAgents.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    {/* Expanded Content */}
                    {expandedSkill === skill.id && (
                      <div className={styles.expandedDetails}>
                        <div className={styles.detailSection}>
                          <h4>Skill Content</h4>
                          <pre className={styles.codeBlock}>{skill.content}</pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ========= OVERVIEW TAB ========= */}
          {activeTab === 'overview' && (
            <div className={styles.overviewContent}>
              {analysis?.overview ? (
                <>
                  {/* Stats */}
                  <div className={styles.overviewStats}>
                    <div className={styles.overviewStatCard}>
                      <div className={styles.overviewStatValue}>
                        {analysis.overview.stats?.fileCount?.toLocaleString() || '—'}
                      </div>
                      <div className={styles.overviewStatLabel}>Files</div>
                    </div>
                    <div className={styles.overviewStatCard}>
                      <div className={styles.overviewStatValue}>
                        {analysis.overview.stats?.linesOfCode?.toLocaleString() || '—'}
                      </div>
                      <div className={styles.overviewStatLabel}>Lines of Code</div>
                    </div>
                    <div className={styles.overviewStatCard}>
                      <div className={styles.overviewStatValue}>
                        {analysis.overview.stats?.dependencyCount?.toLocaleString() || '—'}
                      </div>
                      <div className={styles.overviewStatLabel}>Dependencies</div>
                    </div>
                  </div>

                  {/* Architecture Diagram */}
                  {analysis.overview.architecture && (
                    <div className={styles.overviewSection}>
                      <h3>Architecture</h3>
                      <pre className={styles.codeBlock}>{analysis.overview.architecture}</pre>
                    </div>
                  )}

                  {/* Design Patterns */}
                  {analysis.patterns?.designPatterns?.length > 0 && (
                    <div className={styles.overviewSection}>
                      <h3>Design Patterns</h3>
                      <div className={styles.patternsList}>
                        {analysis.patterns.designPatterns.map((pattern, idx) => (
                          <div key={idx} className={styles.patternCard}>
                            <strong>{pattern.name}</strong>
                            <p>{pattern.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Libraries */}
                  {analysis.dependencies?.keyLibraries?.length > 0 && (
                    <div className={styles.overviewSection}>
                      <h3>Key Libraries</h3>
                      <div className={styles.librariesGrid}>
                        {analysis.dependencies.keyLibraries.map((lib, idx) => (
                          <div key={idx} className={styles.libraryCard}>
                            <div className={styles.libraryName}>{lib.name} <span>{lib.version}</span></div>
                            <div className={styles.libraryUsage}>{lib.usage}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.emptyTab}>
                  <Layers size={40} />
                  <h3>No analysis data available</h3>
                  <p>Run an analysis to see codebase overview</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ========= MODALS ========= */}

        {/* Edit Agent Modal */}
        {editingAgent && (
          <EditAgentModal
            agent={editingAgent}
            onSave={handleAgentSave}
            onClose={() => setEditingAgent(null)}
          />
        )}

        {/* Edit Skill Modal */}
        {editingSkill && (
          <EditSkillModal
            skill={editingSkill}
            onSave={handleSkillSave}
            onClose={() => setEditingSkill(null)}
          />
        )}

        {/* Push to Repo Modal */}
        {showPushModal && repo.analysisId && (
          <PushToRepoModal
            analysisId={repo.analysisId}
            repoName={repo.fullName}
            onClose={() => setShowPushModal(false)}
            onComplete={handlePushComplete}
          />
        )}

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <Modal
            isOpen={true}
            onClose={() => setDeleteConfirm(null)}
            title={`Delete ${deleteConfirm.type === 'agent' ? 'Agent' : 'Skill'}`}
            size="sm"
            footer={
              <div className={styles.modalFooter}>
                <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDelete}
                  isLoading={isDeleting}
                >
                  Delete
                </Button>
              </div>
            }
          >
            <p className={styles.deleteText}>
              Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
              This action cannot be undone.
            </p>
          </Modal>
        )}
      </div>
    </AppLayout>
  );
};

export default RepoDetailsPage;
